import { ScoreCategory } from '../models/ScoreTypes';

// Single source of truth for every Treams-score calculation parameter.
// Scores are on the technical 0-9 scale. The product shows that as 0-100
// (score / 9 * 100). Params marked "scale-defining" shape the scale itself —
// tuning them per workspace would make scores incomparable and break the
// fixed 0-9 assumption in the display color bands.

// ---- Category weights ----
export const SCORE_WEIGHTS: Record<ScoreCategory, number> = {
	[ScoreCategory.Engagement]: 0.35,
	[ScoreCategory.OneOnOne]: 0.25,
	[ScoreCategory.Targets]: 0.15,
	[ScoreCategory.Feedback]: 0.15,
	[ScoreCategory.Conversations]: 0.1,
};

export const SCORE_CATEGORIES: ScoreCategory[] = [
	ScoreCategory.Engagement,
	ScoreCategory.Targets,
	ScoreCategory.Feedback,
	ScoreCategory.OneOnOne,
	ScoreCategory.Conversations,
];

// ---- Time windows (ScoreService) ----
export interface ScoreWindowsConfig {
	// Rolling window ending at the selector end-date; the preceding
	// equal-length window powers the delta.
	rollingWindowMonths: number;
	// Conversations use their own lookback (annual evaluation cadence),
	// again doubled backwards for the delta.
	conversationsLookbackDays: number;
}

export const SCORE_WINDOWS: ScoreWindowsConfig = {
	rollingWindowMonths: 3,
	conversationsLookbackDays: 365,
};

// ---- Which groups get a score row (ScoreService) ----
// Distinct from TARGETS_CONFIG.groupOwnerTypes (goal ownership, REQ-T5):
// same values today, different meaning — keep separate.
export const SCORE_ROW_GROUP_TYPES: ReadonlySet<string> = new Set(['TEAM', 'DEPARTMENT']);

// ---- Engagement ----
export interface EngagementConfig {
	floorScore: number; // scale-defining
	// Strictly above: exactly 40% participation does not qualify.
	participationThreshold: number;
	qualifyingIntervals: ReadonlySet<string>;
}

export const ENGAGEMENT_CONFIG: EngagementConfig = {
	floorScore: 0,
	participationThreshold: 0.4,
	qualifyingIntervals: new Set(['WEEKLY', 'BIWEEKLY', 'MONTHLY']),
};

// ---- 1-on-1 ----
// Gap < maxDays, first match wins; bands must stay ascending by maxDays.
export interface GapBand {
	maxDays: number;
	score: number;
}

export interface OneOnOneConfig {
	// Mutable: applyScoreConfigOverrides replaces the array.
	gapBands: GapBand[];
	floorScore: number; // gap beyond the last band
	meetingTargetType: string;
}

export const ONE_ON_ONE_CONFIG: OneOnOneConfig = {
	gapBands: [
		{ maxDays: 8, score: 9 },
		{ maxDays: 15, score: 7 },
		{ maxDays: 32, score: 5 },
	],
	floorScore: 0,
	meetingTargetType: 'NOTE',
};

// ---- Targets ----
// Outcomes 0 | 5 | 7 | 9 technical (displayed +1 as 1 | 6 | 8 | 10).
export interface TargetsConfig {
	floorScore: number; // scale-defining
	baseScore: number; // scale-defining
	bonusPoints: number; // scale-defining; awarded twice (REQ-T3, REQ-T4)
	// Group goals live at TEAM/DEPARTMENT level (REQ-T5); user goals only
	// count as linked contributors (REQ-T4).
	groupOwnerTypes: ReadonlySet<string>;
	individualOwnerType: string;
}

export const TARGETS_CONFIG: TargetsConfig = {
	floorScore: 0,
	baseScore: 5,
	bonusPoints: 2,
	groupOwnerTypes: new Set(['TEAM', 'DEPARTMENT']),
	individualOwnerType: 'USER',
};

// ---- Feedback ----
export interface FeedbackConfig {
	floorScore: number; // scale-defining
	baseScore: number; // scale-defining
	enableBonuses: boolean; // feature flag; off = every scored user gets baseScore
	bonusPoints: number; // scale-defining; awarded twice (on-time + enough givers)
	onTimeRatio: number; // share of shared responses within dueDate
	minGivers: number; // every request must reach this many shared responses
	sharedStatus: string;
}

export const FEEDBACK_CONFIG: FeedbackConfig = {
	floorScore: 0,
	baseScore: 5,
	enableBonuses: true,
	bonusPoints: 2,
	onTimeRatio: 0.9,
	minGivers: 3,
	sharedStatus: 'SHARED',
};

// ---- Conversations ----
export interface ConversationsConfig {
	floorScore: number; // scale-defining
	// Binary 0/9 on the technical scale (frontend +1 -> 1/10): a prepared/shared
	// evaluation in the lookback is a full 9, none is 0.
	baseScore: number;
	draftStatus: string; // both parties must be non-DRAFT
}

export const CONVERSATIONS_CONFIG: ConversationsConfig = {
	floorScore: 0,
	baseScore: 9,
	draftStatus: 'DRAFT',
};

// ---- Display bands ----
// Cutoffs are on the 0-100 score shown in the product.
export interface TranslationBand {
	min: number;
	key: string;
}

export const SCORE_TRANSLATION_BANDS: ReadonlyArray<TranslationBand> = [
	{ min: 85, key: 'analytics.treamsScore.band.peak' },
	{ min: 75, key: 'analytics.treamsScore.band.inForm' },
	{ min: 65, key: 'analytics.treamsScore.band.inRhythm' },
	{ min: 50, key: 'analytics.treamsScore.band.building' },
	{ min: -Infinity, key: 'analytics.treamsScore.band.starting' },
];

export const scoreTranslation = (score: number): string =>
	SCORE_TRANSLATION_BANDS.find((band) => score >= band.min)!.key;

// ---- Runtime overrides (launch console) ----
// JSON-serializable snapshot of every tunable used by the calculation, so the
// launch console can ship tweaked values to the analytics worker via
// postMessage/localStorage. String/status params (sharedStatus, draftStatus,
// owner types, intervals) are deliberately not overridable.
export interface ScoreConfigOverrides {
	weights: Record<ScoreCategory, number>;
	windows: ScoreWindowsConfig;
	engagement: { participationThreshold: number };
	oneOnOne: { gapBands: GapBand[] };
	targets: { baseScore: number; bonusPoints: number };
	feedback: {
		baseScore: number;
		enableBonuses: boolean;
		bonusPoints: number;
		onTimeRatio: number;
		minGivers: number;
	};
	conversations: { baseScore: number };
}

export const currentScoreConfigOverrides = (): ScoreConfigOverrides => ({
	weights: { ...SCORE_WEIGHTS },
	windows: { ...SCORE_WINDOWS },
	engagement: { participationThreshold: ENGAGEMENT_CONFIG.participationThreshold },
	oneOnOne: { gapBands: ONE_ON_ONE_CONFIG.gapBands.map((band) => ({ ...band })) },
	targets: { baseScore: TARGETS_CONFIG.baseScore, bonusPoints: TARGETS_CONFIG.bonusPoints },
	feedback: {
		baseScore: FEEDBACK_CONFIG.baseScore,
		enableBonuses: FEEDBACK_CONFIG.enableBonuses,
		bonusPoints: FEEDBACK_CONFIG.bonusPoints,
		onTimeRatio: FEEDBACK_CONFIG.onTimeRatio,
		minGivers: FEEDBACK_CONFIG.minGivers,
	},
	conversations: { baseScore: CONVERSATIONS_CONFIG.baseScore },
});

// Mutates the live config objects; every scorer reads them at call time, so
// overrides take effect on the next findScoreAnalytics run.
export const applyScoreConfigOverrides = (overrides: ScoreConfigOverrides): void => {
	Object.assign(SCORE_WEIGHTS, overrides.weights);
	Object.assign(SCORE_WINDOWS, overrides.windows);
	ENGAGEMENT_CONFIG.participationThreshold = overrides.engagement.participationThreshold;
	ONE_ON_ONE_CONFIG.gapBands = overrides.oneOnOne.gapBands.map((band) => ({ ...band }));
	TARGETS_CONFIG.baseScore = overrides.targets.baseScore;
	TARGETS_CONFIG.bonusPoints = overrides.targets.bonusPoints;
	FEEDBACK_CONFIG.baseScore = overrides.feedback.baseScore;
	FEEDBACK_CONFIG.enableBonuses = overrides.feedback.enableBonuses;
	FEEDBACK_CONFIG.bonusPoints = overrides.feedback.bonusPoints;
	FEEDBACK_CONFIG.onTimeRatio = overrides.feedback.onTimeRatio;
	FEEDBACK_CONFIG.minGivers = overrides.feedback.minGivers;
	CONVERSATIONS_CONFIG.baseScore = overrides.conversations.baseScore;
};
